import { DEMO_BASE_URL, DEMO_CODE } from './config.js';

/**
 * Turning the demo on from inside the app, rather than from the URL.
 *
 * `?demo` is the link somebody is *given*. `BSNT-DEMO` is the code somebody is
 * *told*, and typing it into the entry form on the ordinary site has to work —
 * otherwise the one credential a reader is likely to remember produces
 * `NOT FOUND` from an API that has never heard of it, which is a dead end that
 * looks like a broken product.
 *
 * So the code itself is the switch. Entry recognises it before it asks the
 * network anything, starts the worker, and carries on down the same flow.
 *
 * Two things happen here and both matter:
 *
 *  - the bridge is started and awaited, so the first request after this cannot
 *    race the worker's boot;
 *  - `?demo` is written into the address bar with `replaceState`, so a reload
 *    lands back in the demo rather than in an ordinary session holding a game id
 *    that only ever existed inside a worker.
 */
export function isDemoCode(code: string): boolean {
  return code.trim().toUpperCase() === DEMO_CODE;
}

let activated = false;

/** Start the demo host on demand and return the base URL to talk to it. */
export async function activateDemo(): Promise<string> {
  if (!activated) {
    // Dynamic, like the `?demo` path: the engine, the host and the scenario stay
    // a chunk that an ordinary session never downloads.
    const { startDemoBridge } = await import('./bridge.js');
    await startDemoBridge();
    activated = true;
  }
  rememberInUrl();
  return DEMO_BASE_URL;
}

function rememberInUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('demo')) return;
    url.searchParams.set('demo', '1');
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // A browser that refuses history rewriting still has a working demo; it
    // just loses it on reload, which is a smaller problem than not starting.
  }
}
